import { useEffect, useRef, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  User,
  Mail,
  Phone,
  Building2,
  CreditCard,
  Store,
  Camera,
  Save,
  Loader2,
  FileDown,
  FileText,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../components/AppLayout';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { banks, mccs } from '../lib/mockData';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { GetCookie, send, TOKEN_NAME } from "../components";
import { generateMnIban } from '../components/service/functions';
import axios from 'axios';

const Profile = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const fileInputRef = useRef(null);
  const [frmData, setFrmData] = useState({
    mcc: "",
    bank_code: "",
    lname: "",
    fname: "",
    email: "",
    phone: "",
    acnt_code: "",
    iban: "",
    businessname: "",
    description: "",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const getData = async () => {
    const res = await send("io0011", {}, setLoading);
    if (res.status === 'error') {
      toast.error(res.message);
      return;
    }
    setFrmData(res);
  }

  useEffect(() => {
    getData();
  }, [])


  const handleChange = (field, value) => {
    setFrmData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();

    for (const key in frmData) {
      if (Object.prototype.hasOwnProperty.call(frmData, key)) {
        formData.append(key, frmData[key]);
      }
    }

    if (file) {
      formData.append("image", file);
    }

    setLoading(true)
    axios.post("/api/v1/front/process", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: 'Bearer ' + GetCookie(TOKEN_NAME),
        'pc': 'io0012',
      },
      transformRequest: formData => formData,
    }).then((res) => {
      if (res.data.response_code === "RC000000") {
        toast.success(t('profileUpdated'));
      } else {
        toast.error(res.data.response);
      }
    }).catch((err) => {
      toast.error(err);
    }).finally(() => {
      setLoading(false);
    })
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // зөвхөн зураг шалгах (optional)
    if (!selectedFile.type.startsWith("image/")) {
      alert("Зөвхөн зураг файл оруулна уу");
      return;
    }

    setFile(selectedFile);

    // хуучин preview цэвэрлэх
    if (preview) URL.revokeObjectURL(preview);

    setPreview(URL.createObjectURL(selectedFile));
  };


  useEffect(() => {
    if (frmData.bank_code && frmData.acnt_code) {
      try {
        const iban = generateMnIban(
          frmData.bank_code,
          frmData.acnt_code,
        );
        handleChange('iban', iban);
      } catch (e) {
        handleChange('iban', "");
      }
    }
  }, [frmData])


  const handleDownloadContract = async () => {

    try {
      setLoadingPdf(true);
      const response = await axios.post('/api/v1/front/process', {}, {
        responseType: 'blob',
        withCredentials: true, // cookie auth бол
        headers: {
          Authorization: 'Bearer ' + GetCookie(TOKEN_NAME),
          'pc': "io0020",
        }
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.headers.filename);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error);
    } finally {
      setLoadingPdf(false);
    }
  };


  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-8 animate-fade-up">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            {t('profileSettings')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('manageAccountSettings')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Image */}
          <div className="card-elevated p-6">
            <h2 className="text-lg font-semibold mb-4">{t('profileImage')}</h2>
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center">
                  {preview ? (
                    <img
                      src={preview}
                      alt="Logo preview"
                      className="mx-auto h-24 object-contain"
                    />
                  ) : (frmData.image ?
                    <img src={`/api/v1/image?id=${frmData.image}`} alt={frmData.fname} width="100" className='rounded-full' />
                    : <User className="h-12 w-12 text-primary" />)
                  }
                </div>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
                >
                  <Camera className="h-4 w-4" />
                  {/* hidden input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              </div>
              <div>
                <p className="font-medium text-foreground">{frmData?.lname + ' ' + frmData?.fname || t('yourName')}</p>
                <p className="text-sm text-muted-foreground">{frmData?.email}</p>
                {user?.statusid * 1 === 2 && (
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-accent-foreground bg-accent px-2 py-1 rounded-full w-fit">
                    <Store className="h-3 w-3" />
                    {t('merchant')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="card-elevated p-6 space-y-5">
            <h2 className="text-lg font-semibold">{t('personalInfo')}</h2>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="fullName">{t('fullName')}</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="fullName"
                    value={frmData.lname + ' ' + frmData.fname}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    className="pl-11"
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={frmData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="pl-11"
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('phoneNumber')}</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    value={frmData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="pl-11"
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 pt-2">
              <div className="space-y-2">
                <Label htmlFor="bankName">{t('bankName')}</Label>
                <Select
                  value={frmData.bank_code}
                  onValueChange={(value) => handleChange('bank_code', value)}
                >
                  <SelectTrigger className="h-11">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder={t('selectBank')} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {banks
                      .slice() // original array-г эвдэхгүй
                      .sort((a, b) => (t(a.name)).localeCompare(t(b.name)))
                      .map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {t(bank.name)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankAccount">{t('bankAccount')}</Label>
                <div className="relative">
                  <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="bankAccount"
                    value={frmData.acnt_code}
                    onChange={(e) => {
                      handleChange('acnt_code', e.target.value)
                    }}
                    className="pl-11"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ibanbankAccount">{t('ibanbankAccount')}</Label>
                <div className="relative">
                  <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="ibanbankAccount"
                    value={frmData.iban}
                    className="pl-11"
                    disabled
                    required
                  />
                </div>
              </div>
            </div>
          </div>


          {frmData?.status === 2 && <div className="card-elevated p-6 space-y-5">
            <h2 className="text-lg font-semibold">{t('merchantSettings')}</h2>

            <div className="space-y-2">
              <Label htmlFor="businessName">{t('businessName')}</Label>
              <div className="relative">
                <Store className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="businessname"
                  value={frmData.businessname}
                  onChange={(e) => handleChange('businessname', e.target.value)}
                  className="pl-11"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mcc">{t("mcc")}</Label>
              <Select value={frmData.mcc} onValueChange={(value) => handleChange('mcc', value)} required>
                <SelectTrigger className="h-11">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder={t("selectMcc")} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {mccs
                    .slice() // original array-г эвдэхгүй
                    .sort((a, b) => (t(a.name)).localeCompare(t(b.name)))
                    .map((mcc) => (
                      <SelectItem key={mcc.id} value={mcc.id}>
                        {t(mcc.name)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessDescription">{t('businessDescription')}</Label>
              <Textarea
                id="description"
                value={frmData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="min-h-[100px] resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('apiDocumentation') || 'API Documentation'}</Label>

              <a
                href="/developers/paylink-api"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 rounded-xl border bg-muted/40 px-4 py-3 text-sm transition hover:bg-muted"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>

                  <div className="min-w-0">
                    <div className="font-medium text-foreground">
                      PayLink API Docs
                    </div>
                    <div className="truncate text-muted-foreground">
                      {window.location.origin}/developers/paylink-api
                    </div>
                  </div>
                </div>

                <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
              </a>
            </div>
            {/* <div className="space-y-2">
              <Label className="text-muted-foreground">{t('yourPayLink')}</Label>
              <div className="bg-muted rounded-xl px-4 py-3 text-sm font-mono text-muted-foreground">
                {window.location.origin}/link/{user.merchantInfo?.payLink}
              </div>
            </div> */}
          </div>}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="submit"
              variant="gradient"
              size="lg"
              disabled={loading}
              className="w-full sm:flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('save')}
                </>
              )}
            </Button>

            <Button
              onClick={handleDownloadContract}
              variant="outline"
              size="lg"
              className="w-full sm:flex-1"
              disabled={loadingPdf}
              type="button"
            >
              {loadingPdf ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                <>
                  <FileDown className="mr-2 h-4 w-4" />
                  {t('downloadContract')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
};

export default Profile;
